
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Trophy, CheckCircle2, Clock, Zap, Target, ArrowRight } from 'lucide-react';

const DecisionReinforcementSection = () => {
  const { isMobile } = useBreakpoint();

  const confirmationPoints = [
    "You know this opportunity is real.",
    "You know AI business cards are the future.", 
    "You know local businesses desperately need this."
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-[#FEE6D8]/20 via-white to-[#003a70]/5" 
      paddingY="py-16 md:py-20 lg:py-24" 
      containerWidth="wide" 
      backgroundPattern="dots" 
      patternColor="#003a70" 
      patternOpacity={3}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            Everything You Need Is On
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#FEE6D8] bg-clip-text text-transparent">
              The Other Side Of Your Purchase
            </span>
          </h2>
        </div>

        {/* Smart Decision Acknowledgment */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/60 shadow-xl max-w-4xl mx-auto relative overflow-hidden">
            {/* Success accent */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#FEE6D8]"></div>
            
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="text-white" size={28} />
                </div>
              </div>
              
              <p className={cn(
                "text-[#003a70] font-bold leading-relaxed",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                You've already made the smart decision to invest in The Genius Card Playbook.
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Points */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="max-w-4xl mx-auto space-y-6">
            {confirmationPoints.map((point, index) => (
              <div 
                key={index} 
                className="flex items-center gap-6 p-6 bg-gradient-to-r from-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-xl border border-[#FEE6D8]/50 shadow-lg"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="text-white" size={24} />
                </div>
                <p className={cn(
                  "text-[#003a70] font-bold flex-1",
                  isMobile ? "text-lg" : "text-xl lg:text-2xl"
                )}>
                  {point}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* The Only Question */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/60 shadow-xl relative overflow-hidden">
              {/* Question accent */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#FEE6D8]"></div>
              
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                    <Target className="text-white" size={32} />
                  </div>
                </div>
                
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed mb-4",
                  isMobile ? "text-lg" : "text-xl lg:text-2xl"
                )}>
                  The only question is:
                </p>
                
                <p className={cn(
                  "text-[#003a70] font-bold leading-tight",
                  isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl"
                )}>
                  How fast do you want to start?
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison: DIY vs Professional */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.8s' }}>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* DIY Path */}
              <div className="bg-gradient-to-br from-[#003a70]/10 to-[#003a70]/5 rounded-2xl p-8 border-2 border-[#003a70]/20 shadow-lg">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-[#003a70]/60 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <Clock className="text-white" size={28} />
                  </div>
                  
                  <div>
                    <h3 className={cn(
                      "font-bold text-[#003a70] mb-6",
                      isMobile ? "text-lg" : "text-xl lg:text-2xl"
                    )}>
                      DIY Approach
                    </h3>
                    
                    <div className="text-center">
                      <p className={cn(
                        "text-[#003a70]/80 font-medium leading-relaxed",
                        isMobile ? "text-base" : "text-lg"
                      )}>
                        You can spend the next month creating materials and hope they work...
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Path */}
              <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-2xl p-8 border-2 border-[#FEE6D8]/60 shadow-lg relative overflow-hidden">
                {/* Success accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#003a70] to-[#FEE6D8]"></div>
                
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <Zap className="text-white" size={28} />
                  </div>
                  
                  <div>
                    <h3 className={cn(
                      "font-bold text-[#003a70] mb-6",
                      isMobile ? "text-lg" : "text-xl lg:text-2xl"
                    )}>
                      Professional Approach
                    </h3>
                    
                    <div className="text-center">
                      <p className={cn(
                        "text-[#003a70] font-bold leading-relaxed",
                        isMobile ? "text-base" : "text-lg"
                      )}>
                        Or you can have everything professionally done and ready to deploy in 24 hours.
                      </p>
                    </div>

                    <div className="mt-6 flex items-center justify-center gap-2">
                      <span className={cn(
                        "bg-gradient-to-r from-[#003a70] to-[#FEE6D8] bg-clip-text text-transparent font-bold",
                        isMobile ? "text-lg" : "text-xl"
                      )}>
                        Ready in 24 hours
                      </span>
                      <ArrowRight className="text-[#003a70]" size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default DecisionReinforcementSection;
